import { MenuItem, TextField } from '@mui/material';
import { orderBy } from 'lodash';
import dayjs from 'dayjs';
import { Controller } from 'react-hook-form';
import type { FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { WorkAgreementDetails } from 'components/types';
import useGetWorkAgreementDetails from 'components/shared/hooks/useGetWorkAgreementDetails';
import {
  StyledDateColumns,
  StyledDateValues,
  StyledDetailColumn,
  StyledDetailText,
  StyledLeftGroup,
  StyledOptionBox,
  StyledOptionRow
} from './ControlledWorkAgreementSelect.styles';
import type { ControlledWorkAgreementSelectProps } from './ControlledWorkAgreementSelect.types';

const formatDate = (date: string) => dayjs(date).format('D MMM YYYY');

const getPrimaryBenefit = (wa: WorkAgreementDetails) =>
  wa.benefits?.find((b) => b.isValidToday) ??
  orderBy(wa.benefits ?? [], ['validFrom'], ['desc'])[0];

export default function ControlledWorkAgreementSelect<T extends FieldValues>({
  control,
  name,
  label,
  allLabel,
  employeeId,
  showFunction = true,
  showOrgUnit = true,
  showDate = true,
  valueField = 'waId'
}: ControlledWorkAgreementSelectProps<T>) {
  const { t } = useTranslation('common');
  const { data: workAgreements, isError } =
    useGetWorkAgreementDetails(employeeId);
  const isLoaded = workAgreements !== undefined;
  const sorted = orderBy(workAgreements ?? [], ['nr'], ['asc']);

  const renderOption = (wa: WorkAgreementDetails) => {
    const benefit = getPrimaryBenefit(wa);
    const hasFuncOrOrg = showFunction || showOrgUnit;
    return (
      <StyledOptionBox>
        <StyledOptionRow>
          <StyledLeftGroup>
            <span>{wa.nr}</span>
            {hasFuncOrOrg && (
              <StyledDetailColumn>
                {showFunction && benefit?.functionDescription && (
                  <StyledDetailText>
                    {benefit.functionDescription}
                  </StyledDetailText>
                )}
                {showOrgUnit && benefit?.brName && (
                  <StyledDetailText>{benefit.brName}</StyledDetailText>
                )}
              </StyledDetailColumn>
            )}
          </StyledLeftGroup>
          {showDate && (
            <StyledDateColumns>
              <StyledDetailColumn>
                {wa.employmentStart && (
                  <StyledDetailText>{t('validFrom')}:</StyledDetailText>
                )}
                {wa.employmentEnd && (
                  <StyledDetailText>{t('validTo')}:</StyledDetailText>
                )}
              </StyledDetailColumn>
              <StyledDateValues>
                {wa.employmentStart && (
                  <StyledDetailText>
                    {formatDate(wa.employmentStart)}
                  </StyledDetailText>
                )}
                {wa.employmentEnd && (
                  <StyledDetailText>
                    {formatDate(wa.employmentEnd)}
                  </StyledDetailText>
                )}
              </StyledDateValues>
            </StyledDateColumns>
          )}
        </StyledOptionRow>
      </StyledOptionBox>
    );
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <TextField
          select
          label={label}
          value={isLoaded ? field.value ?? '' : ''}
          onChange={(e) => {
            const v = e.target.value;
            if (v === '') {
              field.onChange(null);
            } else if (valueField === 'entityId') {
              field.onChange(v);
            } else {
              field.onChange(Number(v));
            }
          }}
          onBlur={field.onBlur}
          inputRef={field.ref}
          error={isError}
          helperText={isError ? t('fetchError') : undefined}
          fullWidth
          size="small"
          variant="outlined"
          slotProps={{
            select: {
              displayEmpty: true,
              renderValue: (value) => {
                if (value === '' || value === null) return allLabel;
                const wa =
                  valueField === 'entityId'
                    ? sorted.find((w) => w.entityId === value)
                    : sorted.find((w) => w.waId === value);
                return wa ? String(wa.nr) : '';
              }
            },
            inputLabel: { shrink: true }
          }}
        >
          <MenuItem value="">{allLabel}</MenuItem>
          {sorted.map((wa) => (
            <MenuItem
              key={wa.waId}
              value={valueField === 'entityId' ? wa.entityId : wa.waId}
            >
              {renderOption(wa)}
            </MenuItem>
          ))}
        </TextField>
      )}
    />
  );
}
